import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
  AreaChart, Area, CartesianGrid,
} from "recharts";
import { TrendingUp, TrendingDown, Wallet, AlertTriangle, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { fmtEUR, fmtDate, monthLabel, currentMonth, INCOME, EXPENSE, CHART_COLORS } from "@/lib/format";

function Metric({ label, value, color, icon: Icon, testid }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
        <Icon size={16} style={{ color }} />
      </div>
      <div className="font-heading text-2xl font-bold mt-2" style={{ color }}>
        {fmtEUR(value)}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const month = currentMonth();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", month],
    queryFn: () => api.get(`/dashboard?month=${month}`).then((r) => r.data),
  });

  if (isLoading || !data) {
    return <div className="text-muted-foreground">A carregar…</div>;
  }

  const trendData = (data.trend || []).map((t) => ({ ...t, label: monthLabel(t.month) }));

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold">Painel</h1>
          <p className="text-sm text-muted-foreground">{monthLabel(month)} · finanças pessoais</p>
        </div>
        <div className="text-right">
          <div className="text-xs text-muted-foreground">Saldo total (todas as contas)</div>
          <div className="font-heading text-xl font-bold text-primary" data-testid="total-balance">
            {fmtEUR(data.total_balance)}
          </div>
        </div>
      </div>

      {/* Alertas de faturas */}
      {data.invoice_alerts?.length > 0 && (
        <div
          className="rounded-lg border border-[#C75D42]/40 bg-[#C75D42]/10 p-4 flex items-start gap-3"
          data-testid="invoice-alert-banner"
        >
          <AlertTriangle className="text-[#C75D42] shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <div className="font-semibold text-[#C75D42]">Faturas a vencer nos próximos 7 dias</div>
            <ul className="mt-1 space-y-0.5 text-sm text-foreground">
              {data.invoice_alerts.map((i) => (
                <li key={i.id} className="flex justify-between">
                  <span>{i.fornecedor} · {fmtDate(i.due_date)}</span>
                  <span className="font-medium">{i.amount ? fmtEUR(i.amount) : "—"}</span>
                </li>
              ))}
            </ul>
          </div>
          <button onClick={() => navigate("/faturas")} className="text-[#C75D42]" data-testid="alert-goto-invoices">
            <ArrowRight size={18} />
          </button>
        </div>
      )}

      {/* Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Metric label="Recebido" value={data.total_income} color={INCOME} icon={TrendingUp} testid="metric-income" />
        <Metric label="Gasto" value={data.total_expense} color={EXPENSE} icon={TrendingDown} testid="metric-expense" />
        <Metric label="Saldo do mês" value={data.balance} color={data.balance >= 0 ? INCOME : EXPENSE} icon={Wallet} testid="metric-balance" />
      </div>

      {/* Contas */}
      <div>
        <h2 className="text-lg font-semibold mb-3 font-heading">Saldo por conta</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.accounts.map((a) => (
            <div key={a.id} className="bg-card border border-border rounded-lg p-4 flex items-center justify-between" data-testid={`account-card-${a.name}`}>
              <div>
                <div className="font-medium">{a.name}</div>
                <div className="text-xs text-muted-foreground">{a.type === "cash" ? "Dinheiro" : "Conta bancária"}</div>
              </div>
              <div className="font-heading text-lg font-bold" style={{ color: a.balance >= 0 ? "#4A5D4E" : EXPENSE }}>
                {fmtEUR(a.balance)}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Despesas por categoria */}
        <div className="bg-card border border-border rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-3 font-heading">Despesas por categoria</h2>
          {data.by_category.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Sem despesas este mês.</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(180, data.by_category.length * 34)}>
              <BarChart data={data.by_category} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="category" width={110} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => fmtEUR(v)} />
                <Bar dataKey="amount" radius={[0, 4, 4, 0]}>
                  {data.by_category.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Tendência */}
        <div className="bg-card border border-border rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-3 font-heading">Tendência (6 meses)</h2>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={trendData} margin={{ left: -20, right: 10 }}>
              <defs>
                <linearGradient id="gInc" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={INCOME} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={INCOME} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={EXPENSE} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={EXPENSE} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DB" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => fmtEUR(v)} />
              <Area type="monotone" dataKey="income" stroke={INCOME} fill="url(#gInc)" name="Recebido" />
              <Area type="monotone" dataKey="expense" stroke={EXPENSE} fill="url(#gExp)" name="Gasto" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {data.detections_pending > 0 && (
        <button
          onClick={() => navigate("/deteccoes")}
          className="w-full text-left rounded-lg border border-primary/40 bg-primary/5 p-3 text-sm text-foreground flex items-center justify-between"
          data-testid="detections-banner"
        >
          <span><b>{data.detections_pending}</b> deteções de email por rever (faturas / despesas).</span>
          <ArrowRight size={16} />
        </button>
      )}

      {data.uncategorized > 0 && (
        <button
          onClick={() => navigate("/historico")}
          className="w-full text-left rounded-lg border border-[#D9A566]/50 bg-[#D9A566]/10 p-3 text-sm text-foreground flex items-center justify-between"
          data-testid="uncategorized-banner"
        >
          <span><b>{data.uncategorized}</b> transações sem categoria este mês — reveja rapidamente.</span>
          <ArrowRight size={16} />
        </button>
      )}
    </div>
  );
}
